/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Withdrawal Detail Modal - Neo-Cyberpunk Financial Terminal
 * ============================================================================
 *
 * A futuristic, high-tech withdrawal request display with holographic effects,
 * animated scan lines, and print-optimized layout.
 */

import React, { useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Printer, CheckCircle, XCircle, Clock, Zap, Wallet, CreditCard, User, FileText } from 'lucide-react';
import { Withdrawal, WithdrawalStatus } from '@/types/superadmin.types';

interface WithdrawalDetailModalProps {
  withdrawal: Withdrawal | null;
  isOpen: boolean;
  onClose: () => void;
}

// Futuristic timestamp formatter
const formatFuturisticDate = (dateStr: string | undefined) => {
  if (!dateStr) return '----.--.--.--:--:--';
  const date = new Date(dateStr);
  return date.toISOString().replace('T', ' // ').replace('Z', ' UTC');
};

// Generate a fake barcode pattern for visual effect
const generateBarcode = (text: string): string[] => {
  const pattern: string[] = [];
  for (let i = 0; i < 40; i++) {
    pattern.push(Math.random() > 0.5 ? 'thin' : 'thick');
  }
  return pattern;
};

// Generate a reference number from withdrawal ID
const generateRefNumber = (id: string | undefined): string => {
  if (!id) return 'WDR-000000';
  return `WDR-${id.slice(0, 6).toUpperCase()}`;
};

// Status configuration with cyberpunk colors
const getStatusConfig = (t: (key: string) => string): Record<WithdrawalStatus, {
  label: string;
  color: string;
  glow: string;
  icon: typeof CheckCircle;
  bgGradient: string;
}> => ({
  PENDING: {
    label: t('withdrawalModal.statusLabels.pending'),
    color: '#ffaa00',
    glow: '0 0 20px rgba(255,170,0,0.5)',
    icon: Clock,
    bgGradient: 'linear-gradient(135deg, rgba(255,170,0,0.1) 0%, transparent 50%)'
  },
  APPROVED: {
    label: t('withdrawalModal.statusLabels.approved'),
    color: '#00aaff',
    glow: '0 0 20px rgba(0,170,255,0.5)',
    icon: CheckCircle,
    bgGradient: 'linear-gradient(135deg, rgba(0,170,255,0.1) 0%, transparent 50%)'
  },
  PAID: {
    label: t('withdrawalModal.statusLabels.paid'),
    color: '#00ff88',
    glow: '0 0 20px rgba(0,255,136,0.5)',
    icon: CheckCircle,
    bgGradient: 'linear-gradient(135deg, rgba(0,255,136,0.1) 0%, transparent 50%)'
  },
  REJECTED: {
    label: t('withdrawalModal.statusLabels.rejected'),
    color: '#ff4466',
    glow: '0 0 20px rgba(255,68,102,0.5)',
    icon: XCircle,
    bgGradient: 'linear-gradient(135deg, rgba(255,68,102,0.1) 0%, transparent 50%)'
  },
});

export const WithdrawalDetailModal: React.FC<WithdrawalDetailModalProps> = ({
  withdrawal,
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation('platform');
  const printRef = useRef<HTMLDivElement>(null);
  const [scanLinePosition, setScanLinePosition] = useState(0);
  const [isAnimating, setIsAnimating] = useState(true);

  // Animated scan line effect
  useEffect(() => {
    if (!isOpen || !isAnimating) return;
    const interval = setInterval(() => {
      setScanLinePosition(prev => (prev + 1) % 100);
    }, 30);
    return () => clearInterval(interval);
  }, [isOpen, isAnimating]);

  // Stop animation after initial reveal
  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      const timeout = setTimeout(() => setIsAnimating(false), 3000);
      return () => clearTimeout(timeout);
    }
  }, [isOpen]);

  const handlePrint = () => {
    const printContent = printRef.current;
    if (!printContent) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Withdrawal Request - ${generateRefNumber(withdrawal?.id)}</title>
          <link href="https://fonts.googleapis.com/css2?family=Orbitron:wght@400;500;600;700;800;900&family=JetBrains+Mono:wght@300;400;500;600&family=Inter:wght@300;400;500;600&display=swap" rel="stylesheet">
          <style>
            ${printStyles}
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  if (!isOpen || !withdrawal) return null;

  const statusConfig = getStatusConfig(t);
  const status = statusConfig[withdrawal.status] || statusConfig.PENDING;
  const StatusIcon = status.icon;
  const barcode = generateBarcode(withdrawal.id);
  const refNumber = generateRefNumber(withdrawal.id);

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div
          className="pointer-events-auto w-full max-w-2xl max-h-[90vh] overflow-auto"
          style={{
            animation: 'modalSlideIn 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
          }}
        >
          {/* Action buttons - outside printable area */}
          <div className="flex justify-end gap-2 mb-3">
            <button
              onClick={handlePrint}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-300"
              style={{
                background: 'linear-gradient(135deg, #1a1a2e 0%, #0f0f1a 100%)',
                border: '1px solid rgba(0,255,255,0.3)',
                color: '#00ffff',
                boxShadow: '0 0 15px rgba(0,255,255,0.2)',
              }}
            >
              <Printer className="w-4 h-4" />
              {t('withdrawalModal.buttons.print')}
            </button>
            <button
              onClick={onClose}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-300"
              style={{
                background: 'linear-gradient(135deg, #2a1a1a 0%, #1a0f0f 100%)',
                border: '1px solid rgba(255,68,102,0.3)',
                color: '#ff4466',
              }}
            >
              <X className="w-4 h-4" />
              {t('withdrawalModal.buttons.close')}
            </button>
          </div>

          {/* Printable Content */}
          <div ref={printRef} className="receipt-container">
            <style>{modalStyles}</style>

            <div className="cyber-receipt">
              {/* Scan line effect */}
              {isAnimating && (
                <div
                  className="scan-line"
                  style={{ top: `${scanLinePosition}%` }}
                />
              )}

              {/* Circuit pattern overlay */}
              <div className="circuit-pattern" />

              {/* Header */}
              <div className="receipt-header">
                <div className="header-left">
                  <div className="logo-container">
                    <div className="logo-hex">
                      <Zap className="logo-icon" />
                    </div>
                    <div className="logo-text">
                      <span className="brand-name">{t('withdrawalModal.header.brand')}</span>
                      <span className="brand-sub">{t('withdrawalModal.header.subtitle')}</span>
                    </div>
                  </div>
                </div>
                <div className="header-right">
                  <div className="doc-type">{t('withdrawalModal.header.docType')}</div>
                  <div className="doc-id">{refNumber}</div>
                </div>
              </div>

              {/* Status Banner */}
              <div
                className="status-banner"
                style={{
                  borderColor: status.color,
                  boxShadow: status.glow,
                  background: status.bgGradient,
                }}
              >
                <div className="status-icon-container" style={{ color: status.color }}>
                  <StatusIcon className="status-icon" />
                  <div className="status-ring" style={{ borderColor: status.color }} />
                </div>
                <div className="status-text">
                  <span className="status-label" style={{ color: status.color }}>
                    {status.label}
                  </span>
                  <span className="status-time">
                    {formatFuturisticDate(withdrawal.processedAt || withdrawal.requestedAt)}
                  </span>
                </div>
              </div>

              {/* Main Content Grid */}
              <div className="content-grid">
                {/* Left Column - Partner Information */}
                <div className="detail-section">
                  <div className="section-header">
                    <User className="section-icon" />
                    <span>{t('withdrawalModal.sections.partnerInfo')}</span>
                  </div>
                  <div className="detail-rows">
                    <div className="detail-row">
                      <span className="detail-label">{t('withdrawalModal.fields.partnerName')}</span>
                      <span className="detail-value highlight">{withdrawal.partnerName}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">{t('withdrawalModal.fields.partnerId')}</span>
                      <span className="detail-value mono">{withdrawal.partnerId?.slice(0, 8)}...</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">{t('withdrawalModal.fields.currency')}</span>
                      <span className="detail-value">{withdrawal.currency}</span>
                    </div>
                  </div>
                </div>

                {/* Right Column - Payment Details */}
                <div className="detail-section">
                  <div className="section-header">
                    <CreditCard className="section-icon" />
                    <span>{t('withdrawalModal.sections.payoutDetails')}</span>
                  </div>
                  <div className="detail-rows">
                    <div className="detail-row">
                      <span className="detail-label">{t('withdrawalModal.fields.paymentMethod')}</span>
                      <span className="detail-value">{withdrawal.paymentMethod?.replace('_', ' ')}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">{t('withdrawalModal.fields.accountDetails')}</span>
                      <span className="detail-value mono text-sm">{withdrawal.paymentDetails || '---'}</span>
                    </div>
                    {withdrawal.processedBy && (
                      <div className="detail-row">
                        <span className="detail-label">{t('withdrawalModal.fields.processedBy')}</span>
                        <span className="detail-value">{withdrawal.processedBy}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Notes Section (if present) */}
              {withdrawal.notes && (
                <div className="notes-section">
                  <div className="notes-header">
                    <FileText className="notes-icon" />
                    <span>{t('withdrawalModal.sections.notes')}</span>
                  </div>
                  <div className="notes-content">
                    {withdrawal.notes}
                  </div>
                </div>
              )}

              {/* Amount Display */}
              <div className="amount-section">
                <div className="amount-label">{t('withdrawalModal.amount.label')}</div>
                <div className="amount-value">
                  <span className="currency-symbol">$</span>
                  <span className="amount-number">
                    {withdrawal.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </span>
                  <span className="currency-code">{withdrawal.currency}</span>
                </div>
                <div className="amount-subtext">
                  {t('withdrawalModal.amount.subtext')}
                </div>
              </div>

              {/* Barcode Section */}
              <div className="barcode-section">
                <div className="barcode-container">
                  {barcode.map((bar, i) => (
                    <div
                      key={i}
                      className={`barcode-bar ${bar}`}
                      style={{ animationDelay: `${i * 20}ms` }}
                    />
                  ))}
                </div>
                <div className="barcode-text">{refNumber}</div>
              </div>

              {/* Footer */}
              <div className="receipt-footer">
                <div className="footer-grid">
                  <div className="footer-item">
                    <span className="footer-label">{t('withdrawalModal.footer.requested')}</span>
                    <span className="footer-value">{formatFuturisticDate(withdrawal.requestedAt)}</span>
                  </div>
                  <div className="footer-item">
                    <span className="footer-label">{t('withdrawalModal.footer.processed')}</span>
                    <span className="footer-value">{formatFuturisticDate(withdrawal.processedAt)}</span>
                  </div>
                  <div className="footer-item">
                    <span className="footer-label">{t('withdrawalModal.footer.verification')}</span>
                    <span className="footer-value verification">
                      {withdrawal.id?.slice(0, 12).toUpperCase()}
                    </span>
                  </div>
                </div>
                <div className="footer-line" />
                <div className="footer-disclaimer">
                  {t('withdrawalModal.footer.disclaimer')}
                  <br />
                  {t('withdrawalModal.footer.contactSupport', { refNumber })}
                </div>
                <div className="footer-brand">
                  <span className="powered-by">{t('withdrawalModal.footer.poweredBy')}</span>
                  <span className="brand">COREX-SUITE</span>
                  <span className="year">&copy; {new Date().getFullYear()}</span>
                </div>
              </div>

              {/* Corner decorations */}
              <div className="corner corner-tl" />
              <div className="corner corner-tr" />
              <div className="corner corner-bl" />
              <div className="corner corner-br" />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// Modal styles (displayed in browser)
const modalStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@400;500;600;700;800;900&family=JetBrains+Mono:wght@300;400;500;600&family=Inter:wght@300;400;500;600&display=swap');

  @keyframes modalSlideIn {
    from {
      opacity: 0;
      transform: translateY(20px) scale(0.98);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  @keyframes scanLine {
    0% { opacity: 0.8; }
    50% { opacity: 0.4; }
    100% { opacity: 0.8; }
  }

  @keyframes glowPulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.6; }
  }

  @keyframes barReveal {
    from { transform: scaleY(0); }
    to { transform: scaleY(1); }
  }

  .cyber-receipt {
    position: relative;
    background: linear-gradient(180deg, #0a0a12 0%, #0f0f1a 50%, #0a0a12 100%);
    border: 1px solid rgba(170, 102, 255, 0.2);
    border-radius: 4px;
    padding: 0;
    overflow: hidden;
    font-family: 'Inter', sans-serif;
    color: #e0e0e8;
  }

  .scan-line {
    position: absolute;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, transparent, rgba(170,102,255,0.6), transparent);
    box-shadow: 0 0 10px rgba(170,102,255,0.8), 0 0 20px rgba(170,102,255,0.4);
    z-index: 10;
    pointer-events: none;
    animation: scanLine 0.5s ease-in-out infinite;
  }

  .circuit-pattern {
    position: absolute;
    inset: 0;
    background-image:
      linear-gradient(rgba(170,102,255,0.03) 1px, transparent 1px),
      linear-gradient(90deg, rgba(170,102,255,0.03) 1px, transparent 1px);
    background-size: 20px 20px;
    pointer-events: none;
  }

  /* Header */
  .receipt-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding: 24px 28px;
    border-bottom: 1px solid rgba(170,102,255,0.1);
    background: linear-gradient(180deg, rgba(170,102,255,0.05) 0%, transparent 100%);
  }

  .logo-container {
    display: flex;
    align-items: center;
    gap: 14px;
  }

  .logo-hex {
    width: 48px;
    height: 48px;
    background: linear-gradient(135deg, #aa66ff 0%, #7733cc 100%);
    clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%);
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 0 20px rgba(170,102,255,0.4);
  }

  .logo-icon {
    width: 24px;
    height: 24px;
    color: #0a0a12;
  }

  .logo-text {
    display: flex;
    flex-direction: column;
  }

  .brand-name {
    font-family: 'Orbitron', sans-serif;
    font-weight: 800;
    font-size: 22px;
    letter-spacing: 4px;
    color: #aa66ff;
    text-shadow: 0 0 10px rgba(170,102,255,0.5);
  }

  .brand-sub {
    font-family: 'JetBrains Mono', monospace;
    font-size: 9px;
    letter-spacing: 3px;
    color: rgba(170,102,255,0.5);
    margin-top: 2px;
  }

  .header-right {
    text-align: right;
  }

  .doc-type {
    font-family: 'Orbitron', sans-serif;
    font-weight: 600;
    font-size: 11px;
    letter-spacing: 3px;
    color: rgba(255,255,255,0.4);
    margin-bottom: 4px;
  }

  .doc-id {
    font-family: 'JetBrains Mono', monospace;
    font-weight: 600;
    font-size: 16px;
    color: #aa66ff;
    letter-spacing: 1px;
  }

  /* Status Banner */
  .status-banner {
    display: flex;
    align-items: center;
    gap: 16px;
    margin: 20px 28px;
    padding: 16px 20px;
    border: 1px solid;
    border-radius: 4px;
    position: relative;
  }

  .status-icon-container {
    position: relative;
    width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .status-icon {
    width: 28px;
    height: 28px;
    z-index: 1;
  }

  .status-ring {
    position: absolute;
    inset: 0;
    border: 2px solid;
    border-radius: 50%;
    animation: glowPulse 2s ease-in-out infinite;
  }

  .status-text {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .status-label {
    font-family: 'Orbitron', sans-serif;
    font-weight: 700;
    font-size: 14px;
    letter-spacing: 2px;
  }

  .status-time {
    font-family: 'JetBrains Mono', monospace;
    font-size: 11px;
    color: rgba(255,255,255,0.4);
  }

  /* Content Grid */
  .content-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
    padding: 0 28px;
  }

  .detail-section {
    background: rgba(0,0,0,0.3);
    border: 1px solid rgba(170,102,255,0.1);
    border-radius: 4px;
    overflow: hidden;
  }

  .section-header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 14px;
    background: rgba(170,102,255,0.05);
    border-bottom: 1px solid rgba(170,102,255,0.1);
    font-family: 'Orbitron', sans-serif;
    font-weight: 600;
    font-size: 10px;
    letter-spacing: 2px;
    color: rgba(170,102,255,0.7);
  }

  .section-icon {
    width: 14px;
    height: 14px;
  }

  .detail-rows {
    padding: 14px;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .detail-row {
    display: flex;
    flex-direction: column;
    gap: 3px;
  }

  .detail-label {
    font-family: 'JetBrains Mono', monospace;
    font-size: 9px;
    letter-spacing: 1px;
    color: rgba(255,255,255,0.35);
  }

  .detail-value {
    font-size: 13px;
    color: #e0e0e8;
  }

  .detail-value.highlight {
    color: #ffffff;
    font-weight: 500;
  }

  .detail-value.mono {
    font-family: 'JetBrains Mono', monospace;
    font-size: 12px;
    color: rgba(170,102,255,0.8);
  }

  .detail-value.text-sm {
    font-size: 11px;
  }

  /* Notes Section */
  .notes-section {
    margin: 20px 28px 0;
    background: rgba(0,0,0,0.3);
    border: 1px solid rgba(170,102,255,0.1);
    border-radius: 4px;
    overflow: hidden;
  }

  .notes-header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 14px;
    background: rgba(170,102,255,0.05);
    border-bottom: 1px solid rgba(170,102,255,0.1);
    font-family: 'Orbitron', sans-serif;
    font-weight: 600;
    font-size: 10px;
    letter-spacing: 2px;
    color: rgba(170,102,255,0.7);
  }

  .notes-icon {
    width: 14px;
    height: 14px;
  }

  .notes-content {
    padding: 14px;
    font-size: 12px;
    color: rgba(255,255,255,0.7);
    line-height: 1.6;
    font-family: 'JetBrains Mono', monospace;
  }

  /* Amount Section */
  .amount-section {
    margin: 24px 28px;
    padding: 24px;
    background: linear-gradient(135deg, rgba(170,102,255,0.08) 0%, rgba(119,51,204,0.05) 100%);
    border: 1px solid rgba(170,102,255,0.2);
    border-radius: 4px;
    text-align: center;
    position: relative;
  }

  .amount-section::before {
    content: '';
    position: absolute;
    top: -1px;
    left: 20%;
    right: 20%;
    height: 2px;
    background: linear-gradient(90deg, transparent, #aa66ff, transparent);
  }

  .amount-label {
    font-family: 'Orbitron', sans-serif;
    font-size: 10px;
    letter-spacing: 4px;
    color: rgba(170,102,255,0.6);
    margin-bottom: 12px;
  }

  .amount-value {
    display: flex;
    align-items: baseline;
    justify-content: center;
    gap: 8px;
  }

  .currency-symbol {
    font-family: 'Orbitron', sans-serif;
    font-size: 28px;
    font-weight: 600;
    color: rgba(170,102,255,0.6);
  }

  .amount-number {
    font-family: 'Orbitron', sans-serif;
    font-size: 48px;
    font-weight: 800;
    color: #aa66ff;
    text-shadow: 0 0 30px rgba(170,102,255,0.5);
    letter-spacing: 2px;
  }

  .currency-code {
    font-family: 'JetBrains Mono', monospace;
    font-size: 14px;
    color: rgba(255,255,255,0.3);
    align-self: flex-end;
    margin-bottom: 8px;
  }

  .amount-subtext {
    font-family: 'JetBrains Mono', monospace;
    font-size: 9px;
    letter-spacing: 2px;
    color: rgba(255,255,255,0.25);
    margin-top: 12px;
  }

  /* Barcode */
  .barcode-section {
    padding: 20px 28px;
    text-align: center;
  }

  .barcode-container {
    display: flex;
    justify-content: center;
    align-items: flex-end;
    gap: 2px;
    height: 50px;
    margin-bottom: 8px;
  }

  .barcode-bar {
    background: linear-gradient(180deg, #aa66ff 0%, rgba(170,102,255,0.6) 100%);
    border-radius: 1px;
    animation: barReveal 0.5s ease-out forwards;
    transform-origin: bottom;
  }

  .barcode-bar.thin {
    width: 2px;
    height: 35px;
  }

  .barcode-bar.thick {
    width: 4px;
    height: 45px;
  }

  .barcode-text {
    font-family: 'JetBrains Mono', monospace;
    font-size: 12px;
    letter-spacing: 4px;
    color: rgba(170,102,255,0.6);
  }

  /* Footer */
  .receipt-footer {
    padding: 20px 28px 24px;
    background: rgba(0,0,0,0.2);
    border-top: 1px solid rgba(170,102,255,0.1);
  }

  .footer-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
    margin-bottom: 16px;
  }

  .footer-item {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .footer-label {
    font-family: 'JetBrains Mono', monospace;
    font-size: 8px;
    letter-spacing: 1px;
    color: rgba(255,255,255,0.3);
  }

  .footer-value {
    font-family: 'JetBrains Mono', monospace;
    font-size: 10px;
    color: rgba(255,255,255,0.6);
  }

  .footer-value.verification {
    color: #aa66ff;
    font-weight: 600;
  }

  .footer-line {
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(170,102,255,0.2), transparent);
    margin: 16px 0;
  }

  .footer-disclaimer {
    font-size: 9px;
    line-height: 1.6;
    color: rgba(255,255,255,0.3);
    text-align: center;
    margin-bottom: 16px;
  }

  .footer-brand {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 8px;
    font-family: 'Orbitron', sans-serif;
  }

  .powered-by {
    font-size: 8px;
    color: rgba(255,255,255,0.2);
    letter-spacing: 1px;
  }

  .brand {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 3px;
    color: rgba(170,102,255,0.6);
  }

  .year {
    font-size: 8px;
    color: rgba(255,255,255,0.2);
  }

  /* Corner decorations */
  .corner {
    position: absolute;
    width: 20px;
    height: 20px;
    border-color: rgba(170,102,255,0.4);
    border-style: solid;
    border-width: 0;
  }

  .corner-tl {
    top: 8px;
    left: 8px;
    border-top-width: 2px;
    border-left-width: 2px;
  }

  .corner-tr {
    top: 8px;
    right: 8px;
    border-top-width: 2px;
    border-right-width: 2px;
  }

  .corner-bl {
    bottom: 8px;
    left: 8px;
    border-bottom-width: 2px;
    border-left-width: 2px;
  }

  .corner-br {
    bottom: 8px;
    right: 8px;
    border-bottom-width: 2px;
    border-right-width: 2px;
  }
`;

// Print-specific styles
const printStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@400;500;600;700;800;900&family=JetBrains+Mono:wght@300;400;500;600&family=Inter:wght@300;400;500;600&display=swap');

  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  body {
    background: #ffffff;
    padding: 20px;
  }

  .cyber-receipt {
    position: relative;
    background: #ffffff;
    border: 2px solid #0a0a12;
    border-radius: 4px;
    padding: 0;
    overflow: hidden;
    font-family: 'Inter', sans-serif;
    color: #1a1a2e;
    max-width: 600px;
    margin: 0 auto;
  }

  .scan-line,
  .circuit-pattern {
    display: none;
  }

  .receipt-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding: 24px 28px;
    border-bottom: 2px solid #0a0a12;
    background: #f8f8fa;
  }

  .logo-container {
    display: flex;
    align-items: center;
    gap: 14px;
  }

  .logo-hex {
    width: 48px;
    height: 48px;
    background: #0a0a12;
    clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .logo-icon {
    width: 24px;
    height: 24px;
    color: #ffffff;
  }

  .logo-text {
    display: flex;
    flex-direction: column;
  }

  .brand-name {
    font-family: 'Orbitron', sans-serif;
    font-weight: 800;
    font-size: 22px;
    letter-spacing: 4px;
    color: #0a0a12;
  }

  .brand-sub {
    font-family: 'JetBrains Mono', monospace;
    font-size: 9px;
    letter-spacing: 3px;
    color: #666;
    margin-top: 2px;
  }

  .header-right {
    text-align: right;
  }

  .doc-type {
    font-family: 'Orbitron', sans-serif;
    font-weight: 600;
    font-size: 11px;
    letter-spacing: 3px;
    color: #666;
    margin-bottom: 4px;
  }

  .doc-id {
    font-family: 'JetBrains Mono', monospace;
    font-weight: 600;
    font-size: 16px;
    color: #0a0a12;
    letter-spacing: 1px;
  }

  .status-banner {
    display: flex;
    align-items: center;
    gap: 16px;
    margin: 20px 28px;
    padding: 16px 20px;
    border: 2px solid #0a0a12;
    border-radius: 4px;
    background: #f8f8fa;
  }

  .status-icon-container {
    width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #0a0a12;
  }

  .status-icon {
    width: 28px;
    height: 28px;
  }

  .status-ring {
    display: none;
  }

  .status-text {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .status-label {
    font-family: 'Orbitron', sans-serif;
    font-weight: 700;
    font-size: 14px;
    letter-spacing: 2px;
    color: #0a0a12;
  }

  .status-time {
    font-family: 'JetBrains Mono', monospace;
    font-size: 11px;
    color: #666;
  }

  .content-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
    padding: 0 28px;
  }

  .detail-section {
    background: #f8f8fa;
    border: 1px solid #ddd;
    border-radius: 4px;
    overflow: hidden;
  }

  .section-header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 14px;
    background: #eee;
    border-bottom: 1px solid #ddd;
    font-family: 'Orbitron', sans-serif;
    font-weight: 600;
    font-size: 10px;
    letter-spacing: 2px;
    color: #333;
  }

  .section-icon {
    width: 14px;
    height: 14px;
  }

  .detail-rows {
    padding: 14px;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .detail-row {
    display: flex;
    flex-direction: column;
    gap: 3px;
  }

  .detail-label {
    font-family: 'JetBrains Mono', monospace;
    font-size: 9px;
    letter-spacing: 1px;
    color: #888;
  }

  .detail-value {
    font-size: 13px;
    color: #1a1a2e;
  }

  .detail-value.highlight {
    color: #000;
    font-weight: 600;
  }

  .detail-value.mono {
    font-family: 'JetBrains Mono', monospace;
    font-size: 12px;
    color: #333;
  }

  .detail-value.text-sm {
    font-size: 11px;
  }

  .notes-section {
    margin: 20px 28px 0;
    background: #f8f8fa;
    border: 1px solid #ddd;
    border-radius: 4px;
    overflow: hidden;
  }

  .notes-header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 14px;
    background: #eee;
    border-bottom: 1px solid #ddd;
    font-family: 'Orbitron', sans-serif;
    font-weight: 600;
    font-size: 10px;
    letter-spacing: 2px;
    color: #333;
  }

  .notes-icon {
    width: 14px;
    height: 14px;
  }

  .notes-content {
    padding: 14px;
    font-size: 12px;
    color: #333;
    line-height: 1.6;
    font-family: 'JetBrains Mono', monospace;
  }

  .amount-section {
    margin: 24px 28px;
    padding: 24px;
    background: #f8f8fa;
    border: 2px solid #0a0a12;
    border-radius: 4px;
    text-align: center;
  }

  .amount-section::before {
    display: none;
  }

  .amount-label {
    font-family: 'Orbitron', sans-serif;
    font-size: 10px;
    letter-spacing: 4px;
    color: #666;
    margin-bottom: 12px;
  }

  .amount-value {
    display: flex;
    align-items: baseline;
    justify-content: center;
    gap: 8px;
  }

  .currency-symbol {
    font-family: 'Orbitron', sans-serif;
    font-size: 28px;
    font-weight: 600;
    color: #333;
  }

  .amount-number {
    font-family: 'Orbitron', sans-serif;
    font-size: 48px;
    font-weight: 800;
    color: #0a0a12;
    letter-spacing: 2px;
  }

  .currency-code {
    font-family: 'JetBrains Mono', monospace;
    font-size: 14px;
    color: #666;
    align-self: flex-end;
    margin-bottom: 8px;
  }

  .amount-subtext {
    font-family: 'JetBrains Mono', monospace;
    font-size: 9px;
    letter-spacing: 2px;
    color: #888;
    margin-top: 12px;
  }

  .barcode-section {
    padding: 20px 28px;
    text-align: center;
  }

  .barcode-container {
    display: flex;
    justify-content: center;
    align-items: flex-end;
    gap: 2px;
    height: 50px;
    margin-bottom: 8px;
  }

  .barcode-bar {
    background: #0a0a12;
    border-radius: 1px;
  }

  .barcode-bar.thin {
    width: 2px;
    height: 35px;
  }

  .barcode-bar.thick {
    width: 4px;
    height: 45px;
  }

  .barcode-text {
    font-family: 'JetBrains Mono', monospace;
    font-size: 12px;
    letter-spacing: 4px;
    color: #333;
  }

  .receipt-footer {
    padding: 20px 28px 24px;
    background: #f8f8fa;
    border-top: 2px solid #0a0a12;
  }

  .footer-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
    margin-bottom: 16px;
  }

  .footer-item {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .footer-label {
    font-family: 'JetBrains Mono', monospace;
    font-size: 8px;
    letter-spacing: 1px;
    color: #888;
  }

  .footer-value {
    font-family: 'JetBrains Mono', monospace;
    font-size: 10px;
    color: #333;
  }

  .footer-value.verification {
    color: #0a0a12;
    font-weight: 600;
  }

  .footer-line {
    height: 1px;
    background: #ddd;
    margin: 16px 0;
  }

  .footer-disclaimer {
    font-size: 9px;
    line-height: 1.6;
    color: #666;
    text-align: center;
    margin-bottom: 16px;
  }

  .footer-brand {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 8px;
    font-family: 'Orbitron', sans-serif;
  }

  .powered-by {
    font-size: 8px;
    color: #888;
    letter-spacing: 1px;
  }

  .brand {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 3px;
    color: #0a0a12;
  }

  .year {
    font-size: 8px;
    color: #888;
  }

  .corner {
    position: absolute;
    width: 20px;
    height: 20px;
    border-color: #0a0a12;
    border-style: solid;
    border-width: 0;
  }

  .corner-tl {
    top: 8px;
    left: 8px;
    border-top-width: 2px;
    border-left-width: 2px;
  }

  .corner-tr {
    top: 8px;
    right: 8px;
    border-top-width: 2px;
    border-right-width: 2px;
  }

  .corner-bl {
    bottom: 8px;
    left: 8px;
    border-bottom-width: 2px;
    border-left-width: 2px;
  }

  .corner-br {
    bottom: 8px;
    right: 8px;
    border-bottom-width: 2px;
    border-right-width: 2px;
  }
`;

export default WithdrawalDetailModal;
